import React, {
  useEffect,
  useImperativeHandle,
  useState,
  forwardRef,
  useCallback,
} from 'react'
import { Dialog, Text, ActivityIndicator, Button } from 'react-native-paper'
import {
  useArticleQuery,
  useCheckUniqTitleLazyQuery,
  useUpdateArticleMutation,
} from '../../../../generated/graphql'
import { useLLMState } from '../create-article/llm/llm.provider'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import Row from '../../../../@ui/containers/grid/row'
import { MelddTextInput } from '../../../../@ui/form/meldd-input'
import ErrorMessage from '../../../../@ui/typo/error.typo'
import TransferOwnership from './components/transfer-ownership'
import DeleteArticleButton from './components/delete-article.button'
import Toast from 'react-native-root-toast'
import Column from '../../../../@ui/containers/grid/column'
import { View } from 'react-native'
import { useNavigation } from '@react-navigation/native'
import { AppNavigation } from '../../../../navigation/types'
import { MessageType } from '../../../../utils/message-type'
import MelddButton from '../../../../@ui/buttons/meldd.button'
import { useIsMobileDevice } from '../../../../hooks/is-mobile.hook'

export interface PerspectiveSettingsModalProps {
  onClose?: () => void
  onSave?: () => void
  children?: React.ReactNode
}

export interface PerspectiveSettingsModalRefs {
  open: (articleId: string) => void
  close: () => void
  articleId: string
}

type FormShape = {
  minCoRank: string
  isAutoTitle: boolean
  title: string
}

export const PerspectiveSettingsModal = forwardRef<
  PerspectiveSettingsModalRefs,
  PerspectiveSettingsModalProps
>(({ onClose, onSave, children }, ref) => {
  const { t } = useTranslation(['common', 'articles'])
  const [perspectiveId, setPerspectiveId] = useState<string>('')
  const { data, loading } = useArticleQuery({
    variables: { id: perspectiveId },
    skip: !perspectiveId,
  })
  const llmState = useLLMState()
  const [updateArticle, { loading: saving }] = useUpdateArticleMutation()
  const [checkUniqTitle] = useCheckUniqTitleLazyQuery()
  const form = useForm<FormShape>({
    defaultValues: {
      minCoRank: '0',
      title: '',
    },
  })
  const [error, setError] = React.useState<string | null>(null)
  const [visible, setVisible] = useState(false)
  const isMobileDevice = useIsMobileDevice()
  useImperativeHandle(ref, () => ({
    open: (articleId: string) => {
      setPerspectiveId(articleId)
      setVisible(true)
    },
    close: () => setVisible(false),
    articleId: perspectiveId,
  }))

  useEffect(() => {
    if (data?.article) {
      form.reset({
        minCoRank: data.article.minCoRank?.toString() ?? '',
        title: (data.article as any).title ?? '',
      })
    }
  }, [data, form.reset])

  const onSubmit = async (values: FormShape) => {
    try {
      if (values.title.trim() !== data?.article?.title) {
        const isUniqTitle = await checkUniqTitle({
          variables: {
            title: values.title,
            clusterId: data?.article?.clusterId ?? '',
          },
        })
        if (!isUniqTitle.data?.checkUniqTitle) {
          return Toast.show(t('articles:title-not-unique'), MessageType.error)
        }
      }
      await updateArticle({
        variables: {
          input: {
            id: perspectiveId,
            minCoRank:
              values.minCoRank !== '' ? values.minCoRank.toString() : 'none',
            isAutoTitle: false, // On update is no need to use auto title any more is only on creation
            title: values.title,
          },
        },
      })
      setError(null)
      setVisible(false)
      onSave?.()
      onClose?.()
    } catch (e: any) {
      setError(e.message)
    }
  }
  const generateArticleTitle = useCallback(async () => {
    const title = await llmState.generateTitle(
      data?.article?.clusterId ?? '',
      data?.article?.latestContent ?? ''
    )
    form.setValue('title', title)
  }, [data?.article, form.setValue])

  return (
    <Dialog
      visible={visible}
      onDismiss={() => {
        setVisible(false)
        onClose?.()
      }}
    >
      <Dialog.Title>{t('articles:settings')}</Dialog.Title>
      <Dialog.Content>
        {loading ? (
          <View
            style={{
              flex: 1,
              justifyContent: 'center',
              alignItems: 'center',
              marginTop: 20,
            }}
          >
            <ActivityIndicator animating={true} size="large" />
          </View>
        ) : (
          <>
            <Row
              alignItems="center"
              justifyContent="space-between"
              columnGap={4}
            >
              <View style={{ flex: 1 }}>
                <MelddTextInput
                  form={form}
                  name="title"
                  style={{ flex: 1 }}
                  label={t('articles:your-title')}
                />
              </View>
              <MelddButton
                fontSize={12}
                style={{}} // The input has the error empty space and makes it fill not align
                onPress={() => generateArticleTitle()}
                title={t('articles:generate-ai-title')}
                isLoading={llmState.state.loading}
              />
            </Row>

            <Row alignItems="center" gap={8}>
              <MelddTextInput
                form={form}
                name="minCoRank"
                style={{ flex: 1 }}
                keyboardType="numeric"
                helperText={t('articles:min-co-rank-description')}
                label={t('articles:min-co-rank')}
              />
            </Row>
            <ErrorMessage message={error} />
            {children}
          </>
        )}
      </Dialog.Content>
      <Dialog.Actions>
        <Button onPress={onClose}>{t('common:cancel')}</Button>
        <Button onPress={form.handleSubmit(onSubmit)} loading={saving}>
          {t('common:save')}
        </Button>
      </Dialog.Actions>
    </Dialog>
  )
})

export const PerspectiveSettingsModalWithAdvanceSettings = forwardRef<
  PerspectiveSettingsModalRefs,
  PerspectiveSettingsModalProps & {
    perspectiveId: string
  }
>(({ onClose, onSave, perspectiveId }, ref) => {
  const { t } = useTranslation(['common', 'articles'])
  const [showAdvanced, setShowAdvanced] = React.useState(false)
  const navigation = useNavigation<AppNavigation>()

  if (!perspectiveId) {
    return null
  }
  return (
    <PerspectiveSettingsModal ref={ref} onClose={onClose} onSave={onSave}>
      <Row columnGap={4} alignItems="center" style={{ marginBottom: 16 }}>
        <Text onPress={() => setShowAdvanced((v) => !v)}>
          {showAdvanced ? '▼' : '▶'}
          <Text style={{ textDecorationLine: 'underline', paddingLeft: 4 }}>
            {t('articles:advance-settings')}
          </Text>
        </Text>
      </Row>
      {showAdvanced && (
        <Column gap={4}>
          <TransferOwnership
            articleId={perspectiveId}
            onTransferSuccess={() => {
              onClose?.()
            }}
          />
          <Row justifyContent="space-between">
            <Text>{t('articles:delete-perspective')}</Text>
            <View style={{ width: 100 }}>
              <DeleteArticleButton
                articleId={perspectiveId}
                onDeleted={() => {
                  Toast.show(t('articles:perspective-deleted'))
                  onClose?.()
                  // Navigate after a brief delay to ensure modal is closed
                  setTimeout(() => {
                    navigation.navigate('ClusterList', {})
                  }, 100)
                }}
              />
            </View>
          </Row>
        </Column>
      )}
    </PerspectiveSettingsModal>
  )
})
